import styles from './styles.module.css';
import classNames from 'classnames';
import React, { CSSProperties, ReactNode, useEffect, useState, useRef } from 'react';
import { CollapsibleIcon } from 'src/icons/collapsible';
import { AnchorIcon } from 'src/icons/anchor';
import { LinkHelper } from 'src/utils/link-helper';

interface Props {
	initExpanded?: boolean;
	title?: string | null;
	children?: ReactNode;
	style?: CSSProperties;
	/**
	 * id will be used as URI hash
	 * make sure to pass any hash already needed (if any) prepended to this id.
	 * @example in ProgramsAndServices page we are already using hashes, so this id needs to take that into account.
	 */
	id?: string;
}

export const CollapsiblePanel = (props: Props) => {
	const { initExpanded, title, children, style, id } = props;
	const [isExpanded, setIsExpanded] = useState(false);
	const inputRef = useRef<HTMLInputElement>(null);

	const encodedId = id ? LinkHelper.parseInternalLink(id) : undefined;

	useEffect(() => {
		if (initExpanded) {
			setIsExpanded(true);
		}
	}, [initExpanded]);

	let hash: string | undefined = undefined;
	if (typeof window !== 'undefined') {
		hash = window?.location?.hash;
	}

	/**
	 * when there is a hash in the URL, Private Collapsible Panels get added to the DOM after the browser decides that there is no DOM element to scroll to.
	 * As a result we don't get Private Collapsible Panels scrolling into view when we have a URL link to them.
	 * Let's scroll to the Collapsible Panel
	 */
	useEffect(() => {
		if (encodedId && hash === `#${encodedId}`) {
			setIsExpanded(true);
			document?.getElementById(encodedId)?.scrollIntoView();
		}
	}, [hash, encodedId]);

	const onToggleClick = () => {
		setIsExpanded((prev) => !prev);
	};

	const classToggleContent = classNames(
		styles.content,
		{ [styles.contentOpen]: isExpanded },
		{ [styles.contentClose]: !isExpanded }
	);

	const classToggleTitle = classNames(styles.title, { [styles.titleOpen]: isExpanded });

	const onCopyLink = (ev: React.MouseEvent) => {
		if (inputRef) {
			inputRef.current?.select && inputRef.current.select();
			document?.execCommand && document?.execCommand('copy');
			ev.stopPropagation();
		}
	};

	// this may happen when we get a draft collapsible panel with no title on it. We better not show anything than showing an empty container with no title
	if (!title) {
		return null;
	}

	return (
		<div id={encodedId} className={styles.wrapper} style={style}>
			<div className={classToggleTitle} onClick={onToggleClick}>
				<h3 className={styles.h3}>
					{encodedId && typeof window !== 'undefined' && (
						<div className={styles.anchorContainer} onClick={onCopyLink}>
							<AnchorIcon />
							<input
								className={styles.copyInput}
								type="text"
								value={`${window.location.origin}${window.location.pathname}#${encodedId}`}
								ref={inputRef}
								readOnly
							></input>
						</div>
					)}
					{title}
				</h3>
				<div className={styles.toggleButton}>
					<CollapsibleIcon state={isExpanded ? 'open' : 'close'} />
				</div>
			</div>
			<div className={classToggleContent}>{children}</div>
		</div>
	);
};
